import type { Field } from "@/types/Field";

/**
 * Built-in guest fields the "—" (not-set) pick must NOT offer, and that never
 * travel in an update's `clearFields`.
 *
 * MIRROR: main-backend `org/nikita/app/guest/GuestRepository.java`
 * `CLEARABLE_FIELDS` (= `gender`, `emailOptIn`; custom fields clear through
 * `additionalFields.<name>`, `isClearableField`). The backend drops every other
 * base name from `clearFields` silently, so a "—" offered here for a field it
 * will not unset is a save that reports success and changes nothing.
 *
 * `smsOptIn` stays out ON PURPOSE (SMS channel wave, B2): consent is strict —
 * every change is a flip to `true` or `false` that writes its own
 * `guest_consents` row. An `$unset` would erase a recorded consent without a
 * row, so the cockpit offers Yes / No only (filters keep "—" = is empty: that
 * reads, it does not write).
 */
export const NON_CLEARABLE_BUILTIN_FIELDS: ReadonlySet<string> = new Set(["smsOptIn"]);

/** Whether an EDIT cell for this field may offer the "—" clear pick. A custom
 *  field of the same name is a different store and stays clearable. */
export function offersClearPick(field: Pick<Field, "name" | "additionalField"> | null | undefined): boolean {
  if (!field) return true;
  return field.additionalField === true || !NON_CLEARABLE_BUILTIN_FIELDS.has(field.name);
}

/** `clearFields` without the names the backend refuses to unset on purpose.
 *  Keeps `undefined` as `undefined` (no key on the wire). */
export function withoutNonClearable(clearFields: string[] | undefined): string[] | undefined {
  if (!clearFields) return clearFields;
  return clearFields.filter((name) => !NON_CLEARABLE_BUILTIN_FIELDS.has(name));
}
